import * as THREE from "three";

/**
 * Procedurally generate clay surface textures on offscreen canvases.
 *
 * The goal is UNFIRED, WET CLAY — porous, matte, coarse. Not glazed ceramic.
 * Layers:
 *   - Coarse pockmarks / pores (biggest features)
 *   - Horizontal "throwing lines" left by the wheel — deep grooves, uneven
 *   - Fine grit for the earthy micro-texture
 *   - Sparse "scrape" streaks for tool marks
 *
 * Outputs:
 *   - normalMap:   tangent-space normals for lighting perturbation
 *   - roughnessMap: broad variation so reflections break up organically
 *   - aoMap:       ambient occlusion baked from height for shadowed grooves
 */

interface ClayTextures {
  normalMap: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  aoMap: THREE.CanvasTexture;
  dispose: () => void;
}

// Cheap pseudo-random hash for deterministic noise.
function hash(x: number, y: number, seed: number): number {
  const h = Math.sin(x * 12.9898 + y * 78.233 + seed * 43.7581) * 43758.5453;
  return h - Math.floor(h);
}

function smoothNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const tl = hash(xi, yi, seed);
  const tr = hash(xi + 1, yi, seed);
  const bl = hash(xi, yi + 1, seed);
  const br = hash(xi + 1, yi + 1, seed);
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const top = tl * (1 - u) + tr * u;
  const bot = bl * (1 - u) + br * u;
  return top * (1 - v) + bot * v;
}

function fbm(x: number, y: number, seed: number, octaves = 4, persistence = 0.5): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    value += amplitude * smoothNoise(x * frequency, y * frequency, seed + i * 17);
    norm += amplitude;
    amplitude *= persistence;
    frequency *= 2;
  }
  return value / norm;
}

// Worley-ish cellular noise for pockmarks/pores. Higher = closer to a cell center (less porous).
function cellular(x: number, y: number, seed: number, density = 30): number {
  const cellX = Math.floor(x * density);
  const cellY = Math.floor(y * density);
  let minDist = 1.0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cx = cellX + dx;
      const cy = cellY + dy;
      const pointX = (cx + hash(cx, cy, seed)) / density;
      const pointY = (cy + hash(cy, cx, seed + 91)) / density;
      const d = Math.hypot(pointX - x, pointY - y);
      if (d < minDist) minDist = d;
    }
  }
  return minDist * density; // 0..~1
}

/**
 * Build a rich height field for unfired clay.
 * Returns { height: Float32Array, width, height } — height is 0..1.
 */
function buildHeightField(width: number, height: number): Float32Array {
  const field = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const u = x / width;
      const v = y / height;

      // Coarse pockmarks / pores — dark pits scattered across the surface.
      // cellular returns ~0 at pore centers; invert to get a "dimple" map.
      const pores = 1 - Math.min(1, cellular(u, v, 5.5, 22));
      // Sharpen so pores read as depressions with clear edges.
      const poreField = Math.pow(pores, 2.2) * 0.55;

      // Horizontal throwing lines — long, wrapping ridges on the wheel.
      // Sinusoidal ridges modulated by low-freq noise so they wobble organically.
      const ringModulation = fbm(u * 3, v * 12, 4.1, 3);
      const rings = Math.sin(v * Math.PI * 44 + ringModulation * 6) * 0.5 + 0.5;
      // Bias into a "groove" shape — narrow valleys, wide plateaus.
      const grooves = Math.pow(1 - rings, 1.8);
      const throwingLines = grooves * (0.35 + fbm(u * 8, v * 30, 9.3, 2) * 0.35);

      // Medium organic bumps — general lumpy surface.
      const lumps = fbm(u * 14, v * 14, 2.7, 4, 0.55);

      // Fine grit — the sandy micro-texture.
      const grit = fbm(u * 90, v * 90, 15.1, 3, 0.55);

      // Sparse scrape streaks — occasional tool-mark diagonals.
      const scrapeMask = Math.pow(fbm(u * 5, v * 5, 30.7, 2), 3);
      const scrape = fbm(u * 40, v * 220, 44.9, 2) * scrapeMask * 0.4;

      // Combine into height. Throwing lines and pores are subtracted (grooves), lumps + grit added.
      let h = 0.5;
      h += lumps * 0.35 - 0.175;      // ± big lumps
      h += grit * 0.18 - 0.09;         // ± fine grit
      h -= throwingLines * 0.32;       // grooves down
      h -= poreField * 0.42;           // pores dig in
      h -= scrape * 0.15;              // scrape marks dig in

      field[y * width + x] = h;
    }
  }
  return field;
}

/**
 * Convert height field → tangent-space normal map via Sobel.
 */
function heightToNormalCanvas(field: Float32Array, width: number, height: number, strength: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(width, height);
  const sample = (x: number, y: number) => {
    const xi = ((x % width) + width) % width; // wrap horizontally (u direction on lathe)
    const yi = Math.max(0, Math.min(height - 1, y));
    return field[yi * width + xi];
  };
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const tl = sample(x - 1, y - 1);
      const t = sample(x, y - 1);
      const tr = sample(x + 1, y - 1);
      const l = sample(x - 1, y);
      const r = sample(x + 1, y);
      const bl = sample(x - 1, y + 1);
      const b = sample(x, y + 1);
      const br = sample(x + 1, y + 1);
      const dx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const dy = (bl + 2 * b + br) - (tl + 2 * t + tr);
      const nx = -dx * strength;
      const ny = -dy * strength;
      const nz = 1.0;
      const invLen = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz);
      const i = (y * width + x) * 4;
      img.data[i] = Math.round((nx * invLen * 0.5 + 0.5) * 255);
      img.data[i + 1] = Math.round((ny * invLen * 0.5 + 0.5) * 255);
      img.data[i + 2] = Math.round((nz * invLen * 0.5 + 0.5) * 255);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * Roughness from height: deep grooves & pores get slightly less rough (they trap moisture / smooth spots),
 * high spots get very rough (dry, exposed grain). Overall bias is HIGH — this is unfired clay.
 */
function buildRoughnessCanvas(field: Float32Array, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(width, height);

  // Extra blotchy variation independent of height.
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const u = x / width;
      const v = y / height;
      const h = field[y * width + x]; // 0..1 roughly
      const blotch = fbm(u * 8, v * 8, 55.3, 3);
      // Base very rough (0.88), height above 0.5 raises it, below lowers it slightly.
      const heightBias = (h - 0.5) * 0.15;
      const blotchVary = (blotch - 0.5) * 0.18;
      const r = Math.max(0.55, Math.min(1.0, 0.88 + heightBias + blotchVary));
      const g = Math.round(r * 255);
      const i = (y * width + x) * 4;
      img.data[i] = g;
      img.data[i + 1] = g;
      img.data[i + 2] = g;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * Bake a simple AO from the height field — grooves and pores darken.
 * Uses a small neighborhood check: pixels that sit below their neighbors are occluded.
 */
function buildAoCanvas(field: Float32Array, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(width, height);
  const sample = (x: number, y: number) => {
    const xi = ((x % width) + width) % width;
    const yi = Math.max(0, Math.min(height - 1, y));
    return field[yi * width + xi];
  };
  const radius = 3;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const center = sample(x, y);
      let occluded = 0;
      let total = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx === 0 && dy === 0) continue;
          const n = sample(x + dx, y + dy);
          if (n > center) occluded += n - center;
          total++;
        }
      }
      const ao = 1 - Math.min(0.55, (occluded / total) * 5.0);
      const g = Math.round(ao * 255);
      const i = (y * width + x) * 4;
      img.data[i] = g;
      img.data[i + 1] = g;
      img.data[i + 2] = g;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

export function createClayTextures(isMobile: boolean): ClayTextures {
  const w = isMobile ? 512 : 1024;
  const h = isMobile ? 256 : 512;

  const field = buildHeightField(w, h);
  const normalCanvas = heightToNormalCanvas(field, w, h, 5.5);
  const roughnessCanvas = buildRoughnessCanvas(field, w, h);
  const aoCanvas = buildAoCanvas(field, w, h);

  const normalMap = new THREE.CanvasTexture(normalCanvas);
  normalMap.wrapS = THREE.RepeatWrapping;
  normalMap.wrapT = THREE.RepeatWrapping;
  normalMap.repeat.set(2, 1);
  normalMap.anisotropy = 8;
  normalMap.needsUpdate = true;

  const roughnessMap = new THREE.CanvasTexture(roughnessCanvas);
  roughnessMap.wrapS = THREE.RepeatWrapping;
  roughnessMap.wrapT = THREE.RepeatWrapping;
  roughnessMap.repeat.set(2, 1);
  roughnessMap.anisotropy = 8;
  roughnessMap.needsUpdate = true;

  const aoMap = new THREE.CanvasTexture(aoCanvas);
  aoMap.wrapS = THREE.RepeatWrapping;
  aoMap.wrapT = THREE.RepeatWrapping;
  aoMap.repeat.set(2, 1);
  aoMap.anisotropy = 8;
  aoMap.needsUpdate = true;

  return {
    normalMap,
    roughnessMap,
    aoMap,
    dispose: () => {
      normalMap.dispose();
      roughnessMap.dispose();
      aoMap.dispose();
    },
  };
}